import React, { useMemo } from 'react';
import { engineLabel, type Engine } from 'api/analysisApi';
import type { BacktestRuleSet } from 'hooks/useBacktest';
import type {
	BacktestGrade,
	BacktestProposal,
	BacktestRollup,
	ForkListRun,
	ProposalTarget,
} from 'types/history';
import {
	decisiveProposal,
	fmtPrice,
	fmtR,
	gradeLabel,
	gradeShortLabel,
	gradeTone,
	normalizeGrade,
	outcomesByGrade,
	verdictLabel,
	verdictTone,
} from './backtestFormat';
import type { VerdictOutcome } from './backtestFormat';
import './fork.css';

/** The four graded calls, in the engine's own order — the same four the History
 *  table gives columns to, so the two surfaces ask the same question. */
const GRADE_ROWS: BacktestGrade[] = ['best', 'decent', 'risk', 'high_risk'];

/** Not trade grades, so they earn a row only when something landed in them —
 *  mirrors the summary bar's GRADE_EXTRA_COLUMNS. */
const GRADE_EXTRA_ROWS: BacktestGrade[] = ['wait', 'unknown'];

/**
 * One run's column, normalised so the table does not care where the numbers came
 * from.
 *
 * `scored` is the load-bearing flag. A run with a stored backtest contributes its
 * *decisive* proposal — levels and verdict from one object, so this table and the
 * Backtest tab can never disagree. A run without one contributes its `preview`,
 * which is the headline trade, NOT the decisive one: decisiveness ranks verdicts,
 * and there are none yet. Those columns must read "not scored" rather than showing
 * an empty verdict, which would look like a neutral outcome.
 */
interface Column {
	run: ForkListRun;
	isParent: boolean;
	scored: boolean;
	direction: string | null;
	riskLabel: string | null;
	entryLow: number | null;
	entryHigh: number | null;
	stopLoss: number | null;
	/** Pre-widening stop, when the replay used a stop multiplier. */
	stopOriginal: number | null;
	stopMult: number | null;
	targets: ProposalTarget[];
	verdict: BacktestProposal['verdict'] | null;
	realizedR: number | null;
	/**
	 * What each grade of proposal actually did — the same lookup the History
	 * table's Best/Decent/Risk/High Risk columns render.
	 *
	 * This is the part the decisive proposal cannot tell you. A run is one column
	 * but up to four proposals, and "the engine's Best call won while its Risk call
	 * stopped out" is exactly the finding worth comparing between engines. Empty
	 * when the run has no stored backtest — grades are bucketed by *outcome*, and
	 * there are none before a sweep.
	 */
	byGrade: Map<BacktestGrade, VerdictOutcome[]>;
}

function toTargets(p: BacktestProposal): ProposalTarget[] {
	return (p.targets ?? []).map((t) => ({ label: t.label, price: t.price, rr: t.rr }));
}

function buildColumn(
	run: ForkListRun,
	rollup: BacktestRollup | undefined,
	isParent: boolean,
): Column {
	const base = {
		run,
		isParent,
		stopOriginal: null as number | null,
		stopMult: null as number | null,
		// Every proposal the run made, bucketed by its grade — not just the
		// decisive one. Absent without a rollup: a grade bucket holds outcomes.
		byGrade: rollup ? outcomesByGrade(rollup.proposals ?? []) : new Map(),
	};

	// Scored: take everything from the decisive proposal. Its `stop_loss` is the
	// stop the replay ACTUALLY used — already widened when stop_mult > 1 — so
	// showing the run's proposed stop here instead would display a level no trade
	// was ever judged against.
	const decisive = rollup ? decisiveProposal(rollup.proposals ?? []) : null;
	if (decisive) {
		return {
			...base,
			scored: true,
			direction: decisive.direction ?? null,
			riskLabel: decisive.risk_label,
			entryLow: decisive.entry_zone?.[0] ?? null,
			entryHigh: decisive.entry_zone?.[1] ?? null,
			stopLoss: decisive.stop_loss,
			stopOriginal: decisive.stop_original ?? null,
			stopMult: decisive.stop_mult ?? null,
			targets: toTargets(decisive),
			verdict: decisive.verdict,
			realizedR: decisive.realized_R,
		};
	}

	const p = run.preview;
	return {
		...base,
		scored: false,
		direction: p?.direction ?? null,
		riskLabel: p?.risk_label ?? null,
		entryLow: p?.entry_low ?? null,
		entryHigh: p?.entry_high ?? null,
		stopLoss: p?.stop_loss ?? null,
		targets: p?.targets ?? [],
		verdict: null,
		realizedR: null,
	};
}

/** Prices agree to the precision actually shown, so a formatting-only difference
 *  is never flagged as a real one. */
const samePrice = (a: number | null, b: number | null): boolean =>
	a == null || b == null ? a === b : fmtPrice(a) === fmtPrice(b);

interface ForkComparisonProps {
	parent: ForkListRun;
	forks: ForkListRun[];
	rollups: Map<string, BacktestRollup>;
	rules: BacktestRuleSet;
	onOpenAnalysis?: (analysisId: string) => void;
}

/**
 * A parent analysis and its forks, one column each.
 *
 * Fields run down the side and runs across the top — the same shape as the
 * aggregate Compare view, for the same reason: with the field fixed, the eye scans
 * one row and finds the disagreement. Every cell that differs from the parent is
 * marked, because that is the entire question this table exists to answer.
 */
export function ForkComparison({
	parent,
	forks,
	rollups,
	rules,
	onOpenAnalysis,
}: ForkComparisonProps) {
	const columns = useMemo<Column[]>(
		() => [
			buildColumn(parent, rollups.get(parent.id), true),
			...forks.map((f) => buildColumn(f, rollups.get(f.id), false)),
		],
		[parent, forks, rollups],
	);

	if (forks.length === 0) return null;

	const base = columns[0];
	const anyScored = columns.some((c) => c.scored);
	const anyWidened = columns.some((c) => (c.stopMult ?? 1) > 1);

	// The four graded rows always, plus Wait/Other only when some run actually put
	// a proposal there — a permanent row of dashes is noise, but a missing row
	// would hide an outcome the reader can see elsewhere.
	const gradeRows = [
		...GRADE_ROWS,
		...GRADE_EXTRA_ROWS.filter((g) => columns.some((c) => c.byGrade.has(g))),
	];

	/** Marks a fork cell that differs from the parent's. */
	const diff = (c: Column, differs: boolean): string =>
		!c.isParent && differs ? ' fk-diff' : '';

	const cellClass = (c: Column, differs = false, extra = ''): string =>
		`${c.isParent ? 'fk-base' : ''}${diff(c, differs)}${extra ? ` ${extra}` : ''}`.trim();

	return (
		<div className='fk-cmp'>
			<div className='fk-cmp-head'>
				<div className='fk-cmp-title'>This data set, run {columns.length} ways</div>
				<div className='fk-cmp-sub'>
					Every column analysed the identical input, so a difference below is the
					engine — not the market moving.{' '}
					{anyScored ? (
						<>
							Outcomes are scored under entry {rules.window} / close{' '}
							{rules.closeWindow || 'none'}
							{rules.stopMult > 1 ? ` / SL ${rules.stopMult}×` : ''}.
						</>
					) : (
						<>Nothing here has been backtested yet — run a sweep to get outcomes.</>
					)}
				</div>
			</div>

			<div className='fk-cmp-wrap'>
				<table className='fk-cmp-table'>
					<thead>
						<tr>
							<th className='fk-field'>Field</th>
							{columns.map((c) => (
								<th key={c.run.id} className={c.isParent ? 'fk-base' : ''}>
									<div className='fk-col-engine'>
										{engineLabel(c.run.engine as Engine)}
									</div>
									<div className='fk-col-tag'>
										{c.isParent ? 'original' : 'fork'}
										{c.run.status === 'error' && ' · failed'}
										{!c.scored && c.run.status !== 'error' && ' · not scored'}
									</div>
									{onOpenAnalysis && !c.isParent && (
										<button
											className='fk-col-open'
											onClick={() => onOpenAnalysis(c.run.id)}
											title='Open this run'
										>
											open
										</button>
									)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{/* ── The head-to-head ──────────────────────────────────────
						    One trade per run: each engine's most conclusive call, so the
						    columns line up 1:1. The grade section below then widens to
						    every proposal each run made. */}
						<tr className='fk-section'>
							<th className='fk-section-head' colSpan={columns.length + 1}>
								Decisive proposal
							</th>
						</tr>

						<tr>
							<th className='fk-field'>Direction</th>
							{columns.map((c) => {
								const differs = c.direction !== base.direction;
								return (
									<td
										key={c.run.id}
										// Direction disagreement is the difference that matters
										// most — two engines reading the same data opposite ways —
										// so it gets its own, louder treatment.
										className={cellClass(c, false, differs && !c.isParent ? 'fk-diff-strong' : '')}
									>
										{c.direction ?? <span className='fk-none'>—</span>}
									</td>
								);
							})}
						</tr>

						<tr>
							<th className='fk-field'>Grade</th>
							{columns.map((c) => (
								<td
									key={c.run.id}
									className={cellClass(c, c.riskLabel !== base.riskLabel)}
								>
									{c.riskLabel ? (
										<span className={`bt-tone-${gradeTone(normalizeGrade(c.riskLabel))}`}>
											{gradeLabel(normalizeGrade(c.riskLabel))}
										</span>
									) : (
										<span className='fk-none'>—</span>
									)}
								</td>
							))}
						</tr>

						<tr>
							<th className='fk-field'>Entry</th>
							{columns.map((c) => (
								<td
									key={c.run.id}
									className={cellClass(
										c,
										!samePrice(c.entryLow, base.entryLow) ||
											!samePrice(c.entryHigh, base.entryHigh),
									)}
								>
									{c.entryLow != null && c.entryHigh != null ? (
										`${fmtPrice(c.entryLow)} – ${fmtPrice(c.entryHigh)}`
									) : (
										<span className='fk-none'>—</span>
									)}
								</td>
							))}
						</tr>

						<tr>
							<th
								className='fk-field'
								title={
									anyWidened
										? 'The stop the replay used. A widened stop shows the proposed one beneath it.'
										: undefined
								}
							>
								Stop
							</th>
							{columns.map((c) => (
								<td
									key={c.run.id}
									className={cellClass(c, !samePrice(c.stopLoss, base.stopLoss))}
								>
									{c.stopLoss != null ? fmtPrice(c.stopLoss) : <span className='fk-none'>—</span>}
									{/* Only when the replay moved it: otherwise the two are the
									    same number printed twice. */}
									{(c.stopMult ?? 1) > 1 && c.stopOriginal != null && (
										<div className='fk-sub'>
											proposed {fmtPrice(c.stopOriginal)}
										</div>
									)}
								</td>
							))}
						</tr>

						<tr>
							<th className='fk-field' title='First target and its reward-to-risk'>
								TP1 · R:R
							</th>
							{columns.map((c) => {
								const t = c.targets[0];
								const bt = base.targets[0];
								return (
									<td
										key={c.run.id}
										className={cellClass(
											c,
											!samePrice(t?.price ?? null, bt?.price ?? null),
										)}
									>
										{t?.price != null ? (
											<>
												{fmtPrice(t.price)}
												{/* Risk-first, always "1:X" — never "X:1". */}
												{t.rr != null && <span className='fk-rr'> · 1:{t.rr}</span>}
											</>
										) : (
											<span className='fk-none'>—</span>
										)}
									</td>
								);
							})}
						</tr>

						<tr className='fk-row-outcome'>
							<th className='fk-field'>Verdict</th>
							{columns.map((c) => (
								<td key={c.run.id} className={cellClass(c, c.verdict !== base.verdict)}>
									{c.scored && c.verdict ? (
										<span className={`bt-vbadge bt-tone-${verdictTone(c.verdict)}`}>
											{verdictLabel(c.verdict)}
										</span>
									) : (
										// Distinct from a neutral outcome on purpose: an empty cell
										// here would read as "nothing happened" rather than
										// "nobody has scored this".
										<span className='fk-unscored'>
											{c.run.status === 'error' ? 'failed' : 'not scored'}
										</span>
									)}
								</td>
							))}
						</tr>

						<tr>
							<th className='fk-field'>Realized R</th>
							{columns.map((c) => (
								<td key={c.run.id} className={cellClass(c)}>
									{c.scored && c.realizedR != null ? (
										<span className={c.realizedR >= 0 ? 'bt-tone-good' : 'bt-tone-bad'}>
											{fmtR(c.realizedR)}
										</span>
									) : (
										<span className='fk-none'>—</span>
									)}
								</td>
							))}
						</tr>

						<tr>
							<th className='fk-field'>Cost</th>
							{columns.map((c) => (
								<td key={c.run.id} className={cellClass(c)}>
									{c.run.cost_usd != null ? (
										`$${c.run.cost_usd.toFixed(3)}`
									) : (
										<span className='fk-none'>—</span>
									)}
								</td>
							))}
						</tr>

						{/* ── Outcome by grade ──────────────────────────────────────
						    The rows above compare one trade per run; these compare all
						    of them, split by the grade the engine gave itself. That is
						    the question the grades exist to answer — do the calls an
						    engine labels Best actually beat the ones it labels Risk —
						    and it is asked here exactly as the History table asks it. */}
						<tr className='fk-section'>
							<th className='fk-section-head' colSpan={columns.length + 1}>
								Outcome by grade
							</th>
						</tr>

						{gradeRows.map((g) => (
							<tr key={g}>
								<th
									className='fk-field'
									title={`What each run's ${gradeLabel(g)} proposals did`}
								>
									<span className={`bt-tone-${gradeTone(g)}`}>{gradeShortLabel(g)}</span>
								</th>
								{columns.map((c) => {
									const outcomes = c.byGrade.get(g);
									return (
										<td key={c.run.id} className={cellClass(c)}>
											{!c.scored ? (
												// The Verdict row above already said this column is
												// unscored; repeating the words in five cells would
												// drown the rows that do carry information.
												<span className='fk-none'>·</span>
											) : outcomes ? (
												<div className='bt-outcomes'>
													{outcomes.map((o) => (
														// No grade suffix — the row already is the grade.
														<span
															key={o.verdict}
															className={`bt-vbadge bt-tone-${verdictTone(o.verdict)}`}
															title={`${o.count} ${gradeLabel(g)} ${
																o.count === 1 ? 'proposal' : 'proposals'
															} → ${verdictLabel(o.verdict)}`}
														>
															{verdictLabel(o.verdict)}
															{o.count > 1 ? ` ×${o.count}` : ''}
														</span>
													))}
												</div>
											) : (
												// Distinct from "·" above: this run WAS scored and
												// simply made no call at this grade, which is itself a
												// difference between engines worth seeing.
												<span
													className='fk-none'
													title={`This run made no ${gradeLabel(g)} proposal`}
												>
													—
												</span>
											)}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{columns.some((c) => c.run.prose) && (
				<p className='fork-note'>
					A column marked “—” throughout produced free prose rather than structured
					trades, so there is nothing to line up for it.
				</p>
			)}
		</div>
	);
}
